import React, { useState } from 'react';
import {
  Box,
  Paper,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  Google as GoogleIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './LoginPage.css';

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const { currentUser, userProfile, loading: authLoading, handleGoogleLogin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [checkingAuth, setCheckingAuth] = useState(true);

  // Check if user is already logged in
  React.useEffect(() => {
    if (authLoading) return;

    if (!currentUser) {
      setCheckingAuth(false);
      return;
    }

    if (!userProfile) {
      const timeout = setTimeout(() => {
        setCheckingAuth(false);
      }, 3000);
      return () => clearTimeout(timeout);
    }

    setCheckingAuth(false);
    navigate('/admin', { replace: true });

    setTimeout(() => {
      if (window.location.pathname.includes('/login')) {
        window.location.href = '/admin';
      }
    }, 200);
  }, [currentUser, navigate, userProfile, authLoading]);

  const onGoogleLoginClick = async () => {
    setLoading(true);
    setError('');

    try {
      await handleGoogleLogin();
      navigate('/admin', { replace: true });

      setTimeout(() => {
        if (window.location.pathname.includes('/login')) {
          window.location.href = '/admin';
        }
      }, 500);
    } catch (error) {
      console.error('Admin Google login error:', error);
      if (error.code !== 'auth/popup-closed-by-user') {
        setError('Google 로그인 중 오류가 발생했습니다. 다시 시도해주세요.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Show loading while checking auth
  if (authLoading || (currentUser && !userProfile && checkingAuth)) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: '#f5f5f5'
      }}>
        <CircularProgress size={60} sx={{ color: '#FF4444' }} />
      </Box>
    );
  }

  return (
    <Box sx={{
      position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: '#f5f5f5',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'auto'
      }}>
        <Paper elevation={3} sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '420px',
          padding: '48px 40px',
          borderRadius: 2,
          background: 'white',
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)'
        }}>
          {/* Logo */}
          <Box sx={{
            mb: 3,
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            maxWidth: '340px',
            overflow: 'hidden'
          }}>
            <img
              src="/images/logo_hl_w.png"
              alt="HyperLounge"
              style={{
                height: '40px',
                maxWidth: '100%',
                objectFit: 'contain',
                filter: 'brightness(0)'
              }}
            />
          </Box>

          {/* Title */}
          <Typography variant="h5" sx={{
            color: '#333',
            fontWeight: '600',
            mb: 1,
            textAlign: 'center',
            fontSize: '24px'
          }}>
            Ontology Data Dashboard
          </Typography>

          <Typography variant="body2" sx={{
            color: '#888',
            mb: 4,
            textAlign: 'center',
            fontSize: '14px'
          }}>
            System Administration
          </Typography>

          {/* Divider */}
          <Box sx={{
            width: '100%',
            height: '1px',
            bgcolor: '#e0e0e0',
            mb: 4
          }} />

          {/* Login Section Title */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            mb: 2,
            width: '100%'
          }}>
            <Typography variant="h6" sx={{
              color: '#333',
              fontSize: '18px',
              fontWeight: 500
            }}>
              관리자 로그인
            </Typography>
            <Chip label="Admin Only" sx={{
              bgcolor: '#FF4444',
              color: 'white',
              fontWeight: 500,
              fontSize: '11px',
              height: '24px'
            }} size="small" />
          </Box>

          <Typography variant="body2" sx={{
            color: '#888',
            mb: 4,
            textAlign: 'center',
            fontSize: '13px'
          }}>
            Google 계정으로 로그인하세요
          </Typography>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{
              mb: 3,
              width: '100%',
              borderRadius: 2
            }}>
              {error}
            </Alert>
          )}

          {/* Google Login Button */}
          <Box sx={{ width: '100%', mb: 3, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <GoogleIcon />}
              onClick={onGoogleLoginClick}
              disabled={loading}
              sx={{
                backgroundColor: '#FF4444',
                color: 'white',
                textTransform: 'none',
                fontSize: '15px',
                fontWeight: 500,
                padding: '12px 32px',
                borderRadius: '8px',
                minWidth: '260px',
                '&:hover': {
                  backgroundColor: '#E03030'
                },
                '&:disabled': {
                  backgroundColor: '#ffaaaa',
                  color: 'white'
                }
              }}
            >
              {loading ? '로그인 중...' : 'Google 계정으로 로그인'}
            </Button>
          </Box>

          {/* Footer Note */}
          <Box sx={{
            width: '100%',
            pt: 2,
            borderTop: '1px solid #f0f0f0',
            mt: 2
          }}>
            <Typography variant="caption" sx={{
              color: '#999',
              textAlign: 'center',
              fontSize: '11px',
              lineHeight: 1.5,
              display: 'block'
            }}>
              보안 정책: 등록된 Google 계정으로 로그인 가능합니다
            </Typography>
          </Box>
      </Paper>
    </Box>
  );
};

export default AdminLoginPage;
